import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Info, Loader2, Building2, GraduationCap, Save, Lock, EyeOff, ScanLine } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import {
  useGetParametresQuery,
  useCreerParametreMutation,
  useModifierParametreMutation,
  useSupprimerParametreMutation,
  useGetUtilisateursQuery,
} from '../../services/coreApi';
import type { Parametre } from '../../services/coreApi';

const TYPE_BADGE: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  string: 'default',
  number: 'info',
  boolean: 'success',
  json: 'warning',
};

const GeneralTab: React.FC = () => {
  const { data: parametres, isLoading } = useGetParametresQuery();
  const [creer, { isLoading: creating }] = useCreerParametreMutation();
  const [modifier, { isLoading: updating }] = useModifierParametreMutation();
  const [supprimer] = useSupprimerParametreMutation();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Parametre | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState<{ cle: string; valeur: string; description: string; type: 'string' | 'number' | 'boolean' | 'json' }>({ cle: '', valeur: '', description: '', type: 'string' });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ cle: '', valeur: '', description: '', type: 'string' });
    setShowForm(true);
  };

  const openEdit = (p: Parametre) => {
    setEditing(p);
    setForm({ cle: p.cle, valeur: p.valeur, description: p.description, type: p.type });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await modifier({ id: editing.id, data: form }).unwrap();
        showMsg('success', 'Paramètre modifié');
      } else {
        await creer(form).unwrap();
        showMsg('success', 'Paramètre créé');
      }
      setShowForm(false);
    } catch (err: any) {
      showMsg('error', err.data?.detail || err.data?.cle?.[0] || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await supprimer(deleteId).unwrap();
      showMsg('success', 'Paramètre supprimé');
      setDeleteId(null);
    } catch (err: any) {
      showMsg('error', err.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700'
            : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* ── Établissement ── */}
      <InstitutionSection parametres={parametres || []} onSave={async (cle, valeur) => {
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'string', description: '' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'string', description: `Config: ${cle}` }).unwrap();
          }
          showMsg('success', `${cle} mis à jour`);
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} />

      {/* ── Institut (impression officielle) ── */}
      <InstitutImpressionSection parametres={parametres || []} onSave={async (cle, valeur) => {
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'string', description: '' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'string', description: `Impression officielle: ${cle}` }).unwrap();
          }
          showMsg('success', `${cle} mis à jour`);
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} />

      {/* ── Verrouillage Association Copies ── */}
      <AssocVerrousSection parametres={parametres || []} onSave={async (cle, valeur) => {
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'string', description: '' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'string', description: `Verrou Association: ${cle}` }).unwrap();
          }
          showMsg('success', `${cle} mis à jour`);
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} />

      {/* ── Verrouillage Génération relevés correction V2 ── */}
      <GenerationVerrousSection parametres={parametres || []} onSave={async (cle, valeur) => {
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'string', description: '' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'string', description: `Verrou Génération V2: ${cle}` }).unwrap();
          }
          showMsg('success', `${cle} mis à jour`);
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} />

      {/* ── Configuration LMD ── */}
      <LMDSection parametres={parametres || []} onSave={async (cle, valeur) => {
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'number', description: '' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'number', description: `Config LMD: ${cle}` }).unwrap();
          }
          showMsg('success', `${cle} mis à jour`);
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} />

      {/* ── Mode de calcul des moyennes (arithmétique / pondéré) ── */}
      <ModeCalculSection parametres={parametres || []} onSave={async (cle, valeur) => {
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'boolean', description: existing.description || 'Mode de calcul des moyennes' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'boolean', description: 'Active la pondération par crédits pour le calcul des moyennes UE et semestre' }).unwrap();
          }
          showMsg('success', 'Mode de calcul mis à jour');
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} />

      {/* ── Anonymat saisie par code de copie (fix 2026-09-02) ── */}
      {/* ── Mot de passe de confirmation pour "Forcer l'accès" (fix 2026-09-06) ── */}
      <MdpForcerAccesSection />

      <AnonymatCodeSection parametres={parametres || []} onSave={async (valeur) => {
        const cle = 'anonymat_saisie_code_actif';
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'boolean', description: existing.description || 'Anonymat saisie par code de copie' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'boolean', description: 'Quand actif, la saisie par code de copie masque l\'identité de l\'étudiant (matricule, nom, photo). Actif par défaut.' }).unwrap();
          }
          showMsg('success', 'Anonymat mis à jour');
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} />

      {/* ── Sécurité : rôles autorisés à consulter les dépôts de sujets ── */}
      <RolesSujetsSection parametres={parametres || []} onSave={async (valeur) => {
        const cle = 'sujets_roles_autorises';
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'json', description: existing.description || 'Rôles autorisés à consulter les dépôts de sujets' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'json', description: 'Rôles autorisés à consulter et télécharger les dépôts de sujets d\'examen' }).unwrap();
          }
          showMsg('success', 'Rôles autorisés mis à jour');
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} onSaveAgents={async (valeur) => {
        const cle = 'sujets_agents_autorises';
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'json', description: existing.description || 'Agents de scolarité autorisés nommément à consulter les dépôts de sujets' } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'json', description: 'Agents de scolarité autorisés nommément à consulter et télécharger les dépôts de sujets d\'examen (ids utilisateurs)' }).unwrap();
          }
          showMsg('success', 'Agents autorisés mis à jour');
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} onSaveDeblocage={async (valeur) => {
        const cle = 'sujets_agents_deblocage';
        const existing = (parametres || []).find(p => p.cle === cle);
        try {
          if (existing) {
            await modifier({ id: existing.id, data: { cle, valeur, type: 'json', description: existing.description || "Agents autorisés à débloquer un sujet pour l'impression" } }).unwrap();
          } else {
            await creer({ cle, valeur, type: 'json', description: "Agents de scolarité autorisés à débloquer (et reverrouiller) un sujet d'examen pour l'impression (ids utilisateurs)" }).unwrap();
          }
          showMsg('success', 'Agents autorisés à débloquer mis à jour');
        } catch { showMsg('error', 'Erreur de sauvegarde'); }
      }} />

      {/* ── Paramètres avancés ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Paramètres avancés</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Paramètres système personnalisés ({parametres?.length || 0} paramètres)
          </p>
        </div>
        <Button variant="primary" icon={<Plus size={16} />} onClick={openCreate}>
          Ajouter
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clé</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Valeur</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {parametres?.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">{p.cle}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">{p.valeur}</td>
                  <td className="px-4 py-3"><Badge variant={TYPE_BADGE[p.type] || 'default'} size="sm">{p.type}</Badge></td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">{p.description || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="outline" size="sm" icon={<Edit size={14} />} onClick={() => openEdit(p)} />
                      <Button variant="outline" size="sm" icon={<Trash2 size={14} />} onClick={() => setDeleteId(p.id)} className="text-red-600 dark:text-red-400" />
                    </div>
                  </td>
                </tr>
              ))}
              {(!parametres || parametres.length === 0) && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    Aucun paramètre configuré
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* About */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-blue-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Système de Gestion des Notes</h4>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
              Institut International 2i — Application de gestion académique LMD
            </p>
          </div>
        </div>
      </div>

      {/* Modal Ajout/Édition */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-5">
                {editing ? 'Modifier le paramètre' : 'Nouveau paramètre'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Clé</label>
                  <Input
                    value={form.cle}
                    onChange={(e) => setForm({ ...form, cle: e.target.value })}
                    placeholder="ex: seuil_admission"
                    disabled={!!editing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as 'string' | 'number' | 'boolean' | 'json' })}
                  >
                    <option value="string">Chaîne</option>
                    <option value="number">Nombre</option>
                    <option value="boolean">Booléen</option>
                    <option value="json">JSON</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valeur</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono text-sm"
                    rows={form.type === 'json' ? 4 : 2}
                    value={form.valeur}
                    onChange={(e) => setForm({ ...form, valeur: e.target.value })}
                    placeholder={form.type === 'boolean' ? 'true / false' : form.type === 'number' ? '10.0' : ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
                <Button variant="primary" onClick={handleSave} disabled={creating || updating || !form.cle || !form.valeur}>
                  {creating || updating ? <><Loader2 size={16} className="animate-spin" /> Enregistrement...</> : editing ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Suppression */}
      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">Supprimer ce paramètre ?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Cette action est irréversible.</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)}>Annuler</Button>
              <Button variant="danger" onClick={handleDelete}>Supprimer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Institution Settings ──
const INSTITUTION_FIELDS = [
  { cle: 'institution_nom', label: "Nom de l'établissement", placeholder: 'Institut International 2i', colSpan: 2 },
  { cle: 'institution_slogan', label: 'Slogan', placeholder: 'Excellence et Innovation' },
  { cle: 'institution_sigle', label: 'Sigle', placeholder: '2i' },
  { cle: 'institution_adresse', label: 'Adresse', placeholder: 'Brazzaville, République du Congo', colSpan: 2 },
  { cle: 'institution_telephone', label: 'Téléphone', placeholder: '+242 06 XXX XX XX' },
  { cle: 'institution_email', label: 'Email', placeholder: 'contact@2i.cg' },
  { cle: 'institution_bp', label: 'BP', placeholder: 'BP XXX' },
  { cle: 'institution_agrement', label: "N° d'agrément", placeholder: 'S/N° 196 MES-CAB.DG-DAAC du 21/04/2021' },
  { cle: 'institution_chef_scolarite', label: 'Chef de Scolarité', placeholder: 'Donald Christ MBEMBA LOKO' },
  { cle: 'institution_directeur', label: 'Directeur Général', placeholder: '' },
];

function InstitutionSection({ parametres, onSave }: { parametres: Parametre[]; onSave: (cle: string, val: string) => Promise<void> }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    const v: Record<string, string> = {};
    INSTITUTION_FIELDS.forEach(f => {
      const p = parametres.find(p => p.cle === f.cle);
      v[f.cle] = p?.valeur || '';
    });
    setValues(v);
  }, [parametres]);

  const handleSave = async (cle: string) => {
    setSaving(cle);
    await onSave(cle, values[cle] || '');
    setSaving(null);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
        <Building2 size={20} className="text-blue-500" /> Informations de l'Établissement
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {INSTITUTION_FIELDS.map(f => (
          <div key={f.cle} className={f.colSpan === 2 ? 'md:col-span-2' : ''}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{f.label}</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={values[f.cle] || ''}
                onChange={e => setValues(v => ({ ...v, [f.cle]: e.target.value }))}
                placeholder={f.placeholder}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
              />
              <button
                onClick={() => handleSave(f.cle)}
                disabled={saving === f.cle}
                className="px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 transition disabled:opacity-50"
              >
                {saving === f.cle ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Institut (impression officielle) ──
// Ces valeurs remontent l'entête institutionnel des impressions (EDT, PV,
// relevés, brassage…) et diffèrent selon le déploiement (PN vs BZ).
// Elles sont exposées via GET /api/parametres/info-institut/ et utilisées
// dans les 4 générateurs (React preview + PDF + Word + HTML backend).
const INSTITUT_IMPRESSION_FIELDS = [
  { cle: 'institut_ville', label: 'Ville', placeholder: 'Pointe-Noire ou Brazzaville', help: 'Utilisée dans « Fait à … » et suffixe adresse.' },
  { cle: 'institut_telephone', label: 'Téléphone', placeholder: '+242 06 XXX XX XX', help: 'Affiché en entête.' },
  { cle: 'institut_email', label: 'Email', placeholder: 'direction2i@…', colSpan: 2 },
  { cle: 'institut_adresse_rue', label: 'Adresse (rue, sans la ville)', placeholder: '06, RUE LI-LELEMB BASE INDUSTRIELLE', colSpan: 2, help: 'La ville est ajoutée automatiquement à la fin.' },
  { cle: 'institut_chef_scolarite', label: 'Chef de Scolarité (nom complet)', placeholder: 'KIPOUTOU MAVOUNGOU R. H.', colSpan: 2, help: 'Signature au pied des documents officiels.' },
  { cle: 'institut_chef_scolarite_titre', label: 'Titre du Chef de Scolarité', placeholder: 'Le Chef de Service de la Scolarité et des Examens', colSpan: 2 },
  { cle: 'institut_numero_agrement', label: "Numéro d'agrément", placeholder: "Agrément définitif de l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021", colSpan: 2, help: 'Affiché dans l’entête des relevés (identique PN/BZ).' },
  { cle: 'institut_site_web', label: 'Site internet', placeholder: 'www.institut2i.com', help: 'Affiché dans l’entête des relevés.' },
];

function InstitutImpressionSection({ parametres, onSave }: { parametres: Parametre[]; onSave: (cle: string, val: string) => Promise<void> }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    const v: Record<string, string> = {};
    INSTITUT_IMPRESSION_FIELDS.forEach(f => {
      const p = parametres.find(p => p.cle === f.cle);
      v[f.cle] = p?.valeur || '';
    });
    setValues(v);
  }, [parametres]);

  const handleSave = async (cle: string) => {
    setSaving(cle);
    await onSave(cle, values[cle] || '');
    setSaving(null);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-1">
        <Building2 size={20} className="text-amber-500" /> Impression officielle (entêtes / signatures)
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Ces valeurs figurent dans l'entête des EDT, PV, relevés, listes brassées et signatures.
        Elles sont propres au campus (Pointe-Noire vs Brazzaville).
        Laissez vide pour utiliser la valeur par défaut du déploiement.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {INSTITUT_IMPRESSION_FIELDS.map(f => (
          <div key={f.cle} className={f.colSpan === 2 ? 'md:col-span-2' : ''}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{f.label}</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={values[f.cle] || ''}
                onChange={e => setValues(v => ({ ...v, [f.cle]: e.target.value }))}
                placeholder={f.placeholder}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
              />
              <button
                onClick={() => handleSave(f.cle)}
                disabled={saving === f.cle}
                className="px-3 py-2 text-xs font-medium text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 transition disabled:opacity-50"
              >
                {saving === f.cle ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              </button>
            </div>
            {f.help && <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-500">{f.help}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Verrouillage Association Copies ──
// Bloque les toggles « Session ordinaire/rattrapage » et « Semestres impairs/pairs »
// sur la page d'association pour éviter les erreurs des agents (association d'une
// copie NORMAL au mauvais semestre, ou changement de session à leur insu).
// Vide = liberté ; NORMAL / RATTRAPAGE / impair / pair = force + verrouille.
const ASSOC_VERROUS_FIELDS: Array<{
  cle: string;
  label: string;
  help: string;
  choices: Array<{ value: string; label: string }>;
}> = [
  {
    cle: 'assoc_verrou_session_mode',
    label: 'Type de session verrouillé',
    help: 'Force le mode et désactive le bouton dans la page « Association des copies ». Laisse vide pour permettre à l\'agent de changer librement.',
    choices: [
      { value: '', label: '— Libre (aucun verrou) —' },
      { value: 'NORMAL', label: 'Session ordinaire' },
      { value: 'RATTRAPAGE', label: 'Session de rattrapage' },
    ],
  },
  {
    cle: 'assoc_verrou_semestre_parity',
    label: 'Parité du semestre verrouillée',
    help: 'Force la parité et désactive le sélecteur. Utile pendant une session mono-semestre (ex : uniquement S2 en juin).',
    choices: [
      { value: '', label: '— Libre (aucun verrou) —' },
      { value: 'impair', label: 'Semestres impairs (S1/S3/S5)' },
      { value: 'pair', label: 'Semestres pairs (S2/S4/S6)' },
    ],
  },
];

function AssocVerrousSection({ parametres, onSave }: { parametres: Parametre[]; onSave: (cle: string, val: string) => Promise<void> }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    const v: Record<string, string> = {};
    ASSOC_VERROUS_FIELDS.forEach(f => {
      const p = parametres.find(p => p.cle === f.cle);
      v[f.cle] = (p?.valeur || '').trim();
    });
    setValues(v);
  }, [parametres]);

  const handleSave = async (cle: string, val: string) => {
    setSaving(cle);
    setValues(v => ({ ...v, [cle]: val }));
    await onSave(cle, val);
    setSaving(null);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-1">
        <Lock size={20} className="text-red-500" /> Verrouillage — Association des copies
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Bloque les toggles « Session ordinaire/rattrapage » et « Semestres impairs/pairs »
        sur la page d'association des copies. Les agents ne peuvent plus les changer par
        mégarde. Réglez ces verrous avant chaque session d'examens.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ASSOC_VERROUS_FIELDS.map(f => (
          <div key={f.cle}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
              {f.label}
              {values[f.cle] && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-600 dark:text-red-400 ml-1">
                  <Lock size={10} /> ACTIF
                </span>
              )}
            </label>
            <div className="flex gap-2">
              <select
                value={values[f.cle] || ''}
                onChange={e => handleSave(f.cle, e.target.value)}
                disabled={saving === f.cle}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
              >
                {f.choices.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {saving === f.cle && <Loader2 size={16} className="mt-2 animate-spin text-red-500" />}
            </div>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-500">{f.help}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Verrouillage Génération relevés correction V2 ──
// Même logique que AssocVerrous mais pour /ocr/generation-correction-v2 :
// bloque le type d'examen (Ordinaire/Rattrapage) et la parité semestre du
// filtre des relevés existants. L'agent ne peut plus générer un relevé sur
// l'ancien semestre ou l'ancienne session par mégarde.
const GENERATION_VERROUS_FIELDS: Array<{
  cle: string;
  label: string;
  help: string;
  choices: Array<{ value: string; label: string }>;
}> = [
  {
    cle: 'generation_verrou_type_examen',
    label: 'Type d\'examen verrouillé',
    help: 'Force le type d\'examen (Ordinaire ou Rattrapage) et désactive le sélecteur dans « Génération relevés correction V2 ». Vide = libre.',
    choices: [
      { value: '', label: '— Libre (aucun verrou) —' },
      { value: 'normal', label: 'Session ordinaire' },
      { value: 'rattrapage', label: 'Session de rattrapage' },
    ],
  },
  {
    cle: 'generation_verrou_semestre_parity',
    label: 'Parité du semestre verrouillée',
    help: 'Force la parité du filtre relevés (Impairs/Pairs). Utile pour cacher les anciens relevés d\'un autre semestre pendant la session en cours.',
    choices: [
      { value: '', label: '— Libre (aucun verrou) —' },
      { value: 'impair', label: 'Semestres impairs (S1/S3/S5)' },
      { value: 'pair', label: 'Semestres pairs (S2/S4/S6)' },
    ],
  },
];

function GenerationVerrousSection({ parametres, onSave }: { parametres: Parametre[]; onSave: (cle: string, val: string) => Promise<void> }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    const v: Record<string, string> = {};
    GENERATION_VERROUS_FIELDS.forEach(f => {
      const p = parametres.find(p => p.cle === f.cle);
      v[f.cle] = (p?.valeur || '').trim();
    });
    setValues(v);
  }, [parametres]);

  const handleSave = async (cle: string, val: string) => {
    setSaving(cle);
    setValues(v => ({ ...v, [cle]: val }));
    await onSave(cle, val);
    setSaving(null);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-1">
        <Lock size={20} className="text-red-500" /> Verrouillage — Génération relevés correction V2
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Bloque le type d'examen et la parité du semestre sur la page
        <code className="mx-1 px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[11px]">/ocr/generation-correction-v2</code>.
        Cache aussi les anciens relevés d'un semestre différent — les agents ne
        voient et ne peuvent générer que ceux du semestre en cours.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {GENERATION_VERROUS_FIELDS.map(f => (
          <div key={f.cle}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
              {f.label}
              {values[f.cle] && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-600 dark:text-red-400 ml-1">
                  <Lock size={10} /> ACTIF
                </span>
              )}
            </label>
            <div className="flex gap-2">
              <select
                value={values[f.cle] || ''}
                onChange={e => handleSave(f.cle, e.target.value)}
                disabled={saving === f.cle}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
              >
                {f.choices.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {saving === f.cle && <Loader2 size={16} className="mt-2 animate-spin text-red-500" />}
            </div>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-500">{f.help}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── LMD Configuration ──
const LMD_FIELDS = [
  { cle: 'lmd_credits_semestre', label: 'Crédits par semestre', placeholder: '30', unit: 'crédits' },
  { cle: 'lmd_note_validation', label: 'Note de validation UE', placeholder: '10', unit: '/20' },
  { cle: 'lmd_note_eliminatoire', label: 'Note éliminatoire', placeholder: '7', unit: '/20' },
  { cle: 'lmd_coeff_devoir_defaut', label: 'Coefficient devoir (défaut)', placeholder: '0.40', unit: '' },
  { cle: 'lmd_coeff_examen_defaut', label: 'Coefficient examen (défaut)', placeholder: '0.60', unit: '' },
  { cle: 'lmd_seuil_rachat', label: 'Seuil de rachat', placeholder: '9', unit: '/20' },
  { cle: 'lmd_marge_rachat', label: 'Marge de rachat max', placeholder: '1', unit: 'point(s)' },
  { cle: 'lmd_moyenne_passage', label: 'Moyenne de passage', placeholder: '10', unit: '/20' },
];

function LMDSection({ parametres, onSave }: { parametres: Parametre[]; onSave: (cle: string, val: string) => Promise<void> }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    const v: Record<string, string> = {};
    LMD_FIELDS.forEach(f => {
      const p = parametres.find(p => p.cle === f.cle);
      v[f.cle] = p?.valeur || '';
    });
    setValues(v);
  }, [parametres]);

  const handleSave = async (cle: string) => {
    setSaving(cle);
    await onSave(cle, values[cle] || '');
    setSaving(null);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
        <GraduationCap size={20} className="text-purple-500" /> Configuration LMD
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {LMD_FIELDS.map(f => (
          <div key={f.cle}>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{f.label}</label>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={values[f.cle] || ''}
                onChange={e => setValues(v => ({ ...v, [f.cle]: e.target.value }))}
                placeholder={f.placeholder}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 text-center font-mono"
              />
              {f.unit && <span className="self-center text-xs text-gray-400 whitespace-nowrap">{f.unit}</span>}
              <button
                onClick={() => handleSave(f.cle)}
                disabled={saving === f.cle}
                className="px-2 py-2 text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 transition disabled:opacity-50"
              >
                {saving === f.cle ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Mode de calcul (arithmétique simple vs pondéré par crédits) — SPLIT S1/S2 ──
// Fix 2026-08-25 : deux toggles indépendants (impair S1/S3/S5 et pair S2/S4/S6)
// pour changer le mode d'un semestre sans perturber les notes déjà publiées
// de l'autre semestre. Fallback : ancien toggle global `calcul_ponderation_credits`
// reste actif tant que la clé spécifique n'existe pas côté serveur.
function ModeCalculSection({ parametres, onSave }: { parametres: Parametre[]; onSave: (cle: string, val: string) => Promise<void> }) {
  const CLE_GLOBAL = 'calcul_ponderation_credits';
  const paramGlobal = parametres.find(p => p.cle === CLE_GLOBAL);
  const globalActif = (paramGlobal?.valeur || '').toLowerCase().trim();
  const globalOn = ['true', '1', 'oui', 'on'].includes(globalActif);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-1">
        <GraduationCap size={20} className="text-amber-500" /> Mode de calcul des moyennes
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Configurable indépendamment pour les semestres impairs (S1/S3/S5) et pairs (S2/S4/S6).
        Un semestre non explicitement configuré retombe sur le paramètre global historique.
      </p>

      <div className="space-y-3">
        <ToggleSemestre
          cle="calcul_ponderation_credits_s1"
          label="Semestres impairs (S1 / S3 / S5)"
          couleur="blue"
          fallbackGlobal={globalOn}
          parametres={parametres}
          onSave={onSave}
        />
        <ToggleSemestre
          cle="calcul_ponderation_credits_s2"
          label="Semestres pairs (S2 / S4 / S6)"
          couleur="emerald"
          fallbackGlobal={globalOn}
          parametres={parametres}
          onSave={onSave}
        />
      </div>

      <p className="text-[11px] text-gray-400 mt-4 border-t border-gray-100 dark:border-gray-700 pt-3">
        <strong>Attention :</strong> le changement de mode d'un semestre n'affecte
        rien tant que vous ne cliquez pas sur « Recalculer » dans le tableau de délibération.
        Depuis le tableau, choisissez « Recalculer ce semestre uniquement » pour ne pas
        toucher les notes déjà publiées de l'autre parité.
      </p>
    </div>
  );
}

function ToggleSemestre({
  cle, label, couleur, fallbackGlobal, parametres, onSave,
}: {
  cle: string;
  label: string;
  couleur: 'blue' | 'emerald';
  fallbackGlobal: boolean;
  parametres: Parametre[];
  onSave: (cle: string, val: string) => Promise<void>;
}) {
  const param = parametres.find(p => p.cle === cle);
  const raw = (param?.valeur || '').toLowerCase().trim();
  const explicit = param !== undefined && raw !== '';
  const isOn = explicit ? ['true', '1', 'oui', 'on'].includes(raw) : fallbackGlobal;

  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    await onSave(cle, isOn ? 'false' : 'true');
    setSaving(false);
  };

  const dot = couleur === 'blue' ? 'bg-blue-500' : 'bg-emerald-500';
  const border = couleur === 'blue' ? 'border-blue-200 dark:border-blue-800' : 'border-emerald-200 dark:border-emerald-800';

  return (
    <div className={`flex items-start justify-between gap-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-900/40 ${border}`}>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <div className={`w-2 h-2 rounded-full ${dot}`} />
          <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">{label}</div>
          {!explicit && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              hérite du global
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {isOn ? (
            <>
              <strong className="text-amber-600 dark:text-amber-400">Pondération activée</strong> — Σ(moy × crédits) / Σ(crédits).
              Les matières et UE à plus fort crédit pèsent davantage.
            </>
          ) : (
            <>
              <strong className="text-blue-600 dark:text-blue-400">Arithmétique simple</strong> —
              toutes les matières se valent (moyenne classique).
            </>
          )}
        </p>
      </div>
      <button
        onClick={toggle}
        disabled={saving}
        role="switch"
        aria-checked={isOn}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 shrink-0 mt-1 ${
          isOn ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
        title={isOn ? 'Désactiver la pondération pour ce semestre' : 'Activer la pondération pour ce semestre'}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            isOn ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

// ── Sécurité : rôles autorisés à consulter les dépôts de sujets d'examen ──
// Fix 2026-08-27 : suite à fuite sujets PN 17/08/2026, on retire par défaut le
// rôle "agent" et on rend la liste configurable via Parametre.sujets_roles_autorises
// (JSON array de rôles). super_admin est toujours autorisé, même absent de la liste.
const ROLES_DISPONIBLES: Array<{ code: string; label: string; description: string }> = [
  { code: 'admin', label: 'Administrateur', description: 'Accès total (sauf droits super_admin)' },
  { code: 'directeur', label: 'Directeur', description: 'Direction pédagogique / académique' },
  { code: 'agent', label: 'Agent de scolarité', description: '⚠️ Attention — risque de fuite si compte compromis' },
  { code: 'formateur', label: 'Formateur', description: 'Enseignants (voient déjà leurs propres dépôts)' },
];

function RolesSujetsSection({ parametres, onSave, onSaveAgents, onSaveDeblocage }: {
  parametres: Parametre[];
  onSave: (val: string) => Promise<void>;
  onSaveAgents: (val: string) => Promise<void>;
  onSaveDeblocage: (val: string) => Promise<void>;
}) {
  const CLE = 'sujets_roles_autorises';
  const CLE_AGENTS = 'sujets_agents_autorises';
  const CLE_DEBLOCAGE = 'sujets_agents_deblocage';
  const param = parametres.find(p => p.cle === CLE);
  const rolesConfigues: string[] = React.useMemo(() => {
    if (!param) return ['admin', 'directeur']; // défaut backend
    try {
      const parsed = JSON.parse(param.valeur);
      if (Array.isArray(parsed)) return parsed.filter(r => typeof r === 'string');
    } catch {}
    return ['admin', 'directeur'];
  }, [param]);

  const [selection, setSelection] = React.useState<string[]>(rolesConfigues);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setSelection(rolesConfigues); }, [rolesConfigues]);

  // Agents désignés nommément (fix 2026-09-15) : accès sans ouvrir tout le rôle agent.
  const { data: utilisateurs, isLoading: chargementAgents } = useGetUtilisateursQuery();
  const agents = React.useMemo(
    () => (utilisateurs || [])
      .filter(u => u.role === 'agent' && u.actif)
      .sort((a, b) => `${a.nom} ${a.prenoms}`.localeCompare(`${b.nom} ${b.prenoms}`)),
    [utilisateurs],
  );
  const paramAgents = parametres.find(p => p.cle === CLE_AGENTS);
  const agentsConfigures: number[] = React.useMemo(() => {
    if (!paramAgents) return [];
    try {
      const parsed = JSON.parse(paramAgents.valeur);
      if (Array.isArray(parsed)) return parsed.map(Number).filter(n => Number.isInteger(n));
    } catch {}
    return [];
  }, [paramAgents]);
  const [agentsSel, setAgentsSel] = React.useState<number[]>(agentsConfigures);
  React.useEffect(() => { setAgentsSel(agentsConfigures); }, [agentsConfigures]);

  // Agents autorisés à débloquer un sujet pour l'impression (fix 2026-09-16)
  const paramDeblocage = parametres.find(p => p.cle === CLE_DEBLOCAGE);
  const deblocageConfigures: number[] = React.useMemo(() => {
    if (!paramDeblocage) return [];
    try {
      const parsed = JSON.parse(paramDeblocage.valeur);
      if (Array.isArray(parsed)) return parsed.map(Number).filter(n => Number.isInteger(n));
    } catch {}
    return [];
  }, [paramDeblocage]);
  const [deblocageSel, setDeblocageSel] = React.useState<number[]>(deblocageConfigures);
  React.useEffect(() => { setDeblocageSel(deblocageConfigures); }, [deblocageConfigures]);
  const toggleDeblocage = (id: number) => {
    setDeblocageSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const dirtyRoles = selection.slice().sort().join(',') !== rolesConfigues.slice().sort().join(',');
  const cleIds = (ids: number[]) => ids.slice().sort((a, b) => a - b).join(',');
  const dirtyAgents = cleIds(agentsSel) !== cleIds(agentsConfigures);
  const dirtyDeblocage = cleIds(deblocageSel) !== cleIds(deblocageConfigures);
  const dirty = dirtyRoles || dirtyAgents || dirtyDeblocage;
  const agentCoche = selection.includes('agent');

  const toggle = (code: string) => {
    setSelection(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };
  const toggleAgent = (id: number) => {
    setAgentsSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (dirtyRoles) await onSave(JSON.stringify(selection));
      if (dirtyAgents) await onSaveAgents(JSON.stringify(agentsSel.slice().sort((a, b) => a - b)));
      if (dirtyDeblocage) await onSaveDeblocage(JSON.stringify(deblocageSel.slice().sort((a, b) => a - b)));
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-1">
        <Lock size={20} className="text-red-500" /> Sécurité — Accès aux sujets d'examen
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Rôles autorisés à consulter et télécharger les dépôts de sujets d'examen (formateurs + banque).
        Le rôle <strong>super_admin</strong> a toujours accès. Chaque téléchargement est journalisé.
      </p>

      <div className="space-y-2 mb-4">
        {ROLES_DISPONIBLES.map(r => {
          const on = selection.includes(r.code);
          const isAgent = r.code === 'agent';
          return (
            <label key={r.code} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              on
                ? isAgent
                  ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
                  : 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800'
                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
            }`}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(r.code)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{r.description}</div>
              </div>
            </label>
          );
        })}
      </div>

      {agentCoche && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 text-xs">
          ⚠️ Attention : autoriser les agents de scolarité augmente le risque de fuite de sujets.
          Un incident a été confirmé le 17/08/2026 depuis un compte agent. Toute activité reste journalisée.
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Agents de scolarité autorisés nommément</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {agentCoche
            ? 'Le rôle Agent est coché : tous les agents ont déjà accès. Cette liste ne sert que lorsque le rôle est décoché.'
            : 'Seuls les agents cochés ici accèdent aux sujets. Les autres agents restent refusés.'}
        </p>
        {chargementAgents ? (
          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement des agents…</div>
        ) : agents.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-gray-400">Aucun agent actif.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {agents.map(a => {
              const on = agentsSel.includes(a.id);
              return (
                <label key={a.id} className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  on
                    ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }`}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleAgent(a.id)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {`${a.nom || ''} ${a.prenoms || ''}`.trim() || a.username}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {a.username}
                      {a.derniere_connexion ? ` · dernière connexion ${new Date(a.derniere_connexion).toLocaleDateString('fr-FR')}` : ''}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Agents autorisés à débloquer un sujet pour l'impression
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Débloquer rend le sujet téléchargeable. Les administrateurs le peuvent toujours ;
          cochez ici les agents à qui vous confiez aussi ce geste. Un agent coché ici voit
          également les dépôts, et chaque déblocage est journalisé avec son nom.
        </p>
        {chargementAgents ? (
          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement des agents…</div>
        ) : agents.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-gray-400">Aucun agent actif.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {agents.map(a => {
              const on = deblocageSel.includes(a.id);
              return (
                <label key={a.id} className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  on
                    ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }`}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleDeblocage(a.id)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-red-500 focus:ring-red-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {`${a.nom || ''} ${a.prenoms || ''}`.trim() || a.username}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{a.username}</div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {selection.length === 0
            ? 'Aucun rôle sélectionné — seul super_admin aura accès.'
            : `${selection.length} rôle(s) sélectionné(s), en plus de super_admin.`}
          {!agentCoche && ` ${agentsSel.length} agent(s) désigné(s).`}
          {` ${deblocageSel.length} agent(s) peuvent débloquer.`}
        </div>
        <Button
          size="sm"
          disabled={!dirty || saving}
          onClick={handleSave}
          className="bg-amber-500 hover:bg-amber-600 text-white"
        >
          {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}


// ── Anonymat pour la saisie par code de copie (fix 2026-09-02) ──
// Contrôle le paramètre global `anonymat_saisie_code_actif`. Actif par défaut :
// dans la page /saisie-par-code-copie, l'identité (matricule/nom/photo) est
// masquée pour respecter l'anonymat pédagogique des corrections. Seul l'admin
// peut désactiver ici.
function AnonymatCodeSection({ parametres, onSave }: {
  parametres: Parametre[]; onSave: (val: string) => Promise<void>;
}) {
  const CLE = 'anonymat_saisie_code_actif';
  const param = parametres.find(p => p.cle === CLE);
  // Défaut TRUE (aligné avec backend)
  const valActuelle = React.useMemo(() => {
    if (!param) return true;
    return param.valeur.toLowerCase().trim() !== 'false'
      && param.valeur.toLowerCase().trim() !== '0';
  }, [param]);

  const [actif, setActif] = React.useState<boolean>(valActuelle);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { setActif(valActuelle); }, [valActuelle]);

  const dirty = actif !== valActuelle;

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(actif ? 'true' : 'false'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-1">
        <ScanLine size={20} className="text-blue-500" /> Saisie par code de copie — Anonymat
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Contrôle l'affichage de l'identité de l'étudiant (matricule, nom, photo)
        dans la page <strong>« Saisie par code de copie »</strong>.
        Actif par défaut pour respecter l'anonymat pédagogique des corrections.
      </p>

      <label className="flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40">
        <input
          type="checkbox"
          checked={actif}
          onChange={(e) => setActif(e.target.checked)}
          className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
        />
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            {actif ? <><EyeOff size={14} className="text-amber-600" /> Anonymat activé</>
                   : <>Anonymat désactivé (identité visible)</>}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {actif
              ? "Les agents voient uniquement le code de la copie. Ils ne voient ni le matricule, ni le nom, ni la photo. Recommandé pendant la période de correction."
              : "Les agents voient le nom, le matricule et la photo à côté de chaque code de copie."}
          </div>
        </div>
      </label>

      <div className="flex items-center justify-between pt-3 mt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Valeur actuelle : <strong>{valActuelle ? 'Actif' : 'Inactif'}</strong>
        </div>
        <Button
          size="sm"
          disabled={!dirty || saving}
          onClick={handleSave}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}


// ── Mot de passe de confirmation "Forcer l'accès" (fix 2026-09-06) ──
// Défini par un admin dans Paramètres > Général. Requis quand un agent /
// caisse veut activer manuellement l'accès aux résultats d'un étudiant qui
// n'est pas à jour. Backend endpoint : /api/parametres/mdp-forcer-acces/
function MdpForcerAccesSection() {
  const [configured, setConfigured] = React.useState<boolean | null>(null);
  const [mdp1, setMdp1] = React.useState('');
  const [mdp2, setMdp2] = React.useState('');
  const [showEdit, setShowEdit] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const charger = React.useCallback(async () => {
    try {
      const r = await fetch('/api/parametres/mdp-forcer-acces/', {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('access_token') || ''}` },
      });
      const j = await r.json();
      setConfigured(!!j.configured);
    } catch { setConfigured(false); }
  }, []);
  React.useEffect(() => { charger(); }, [charger]);

  const handleSave = async () => {
    if (mdp1.length < 4) { setMsg({ type: 'err', text: 'Minimum 4 caractères.' }); return; }
    if (mdp1 !== mdp2) { setMsg({ type: 'err', text: 'Les 2 mots de passe ne correspondent pas.' }); return; }
    setSaving(true); setMsg(null);
    try {
      const r = await fetch('/api/parametres/mdp-forcer-acces/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('access_token') || ''}`,
        },
        body: JSON.stringify({ mot_de_passe: mdp1 }),
      });
      if (!r.ok) throw new Error((await r.json())?.detail || 'Erreur');
      setMsg({ type: 'ok', text: 'Mot de passe enregistré.' });
      setMdp1(''); setMdp2(''); setShowEdit(false);
      await charger();
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message || 'Erreur' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer le mot de passe de confirmation ? Les agents ne pourront plus forcer d\'accès jusqu\'à ce qu\'un nouveau soit défini.')) return;
    setSaving(true);
    try {
      const r = await fetch('/api/parametres/mdp-forcer-acces/', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('access_token') || ''}` },
      });
      if (!r.ok) throw new Error('Erreur');
      setMsg({ type: 'ok', text: 'Mot de passe supprimé.' });
      await charger();
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message || 'Erreur' });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-1">
        <Lock size={20} className="text-orange-500" /> Confirmation « Forcer l'accès »
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Mot de passe demandé aux agents de scolarité et de caisse pour
        confirmer <strong>l'activation manuelle</strong> de l'accès aux résultats
        d'un étudiant qui n'est pas à jour de ses paiements. Les super_admin /
        admin ne sont pas soumis à cette confirmation.
      </p>

      <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 mb-3">
        <div className="flex items-center gap-2">
          {configured === null ? (
            <><Loader2 size={14} className="animate-spin text-gray-400" /> <span className="text-xs text-gray-500">Chargement…</span></>
          ) : configured ? (
            <>
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Mot de passe configuré</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">(non affichable pour sécurité)</span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Aucun mot de passe défini — les agents ne peuvent pas forcer d'accès
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => { setShowEdit(true); setMdp1(''); setMdp2(''); setMsg(null); }}
            className="bg-orange-500 hover:bg-orange-600 text-white">
            {configured ? 'Modifier' : 'Définir'}
          </Button>
          {configured && (
            <Button size="sm" variant="secondary" onClick={handleDelete}
              className="text-red-600 hover:bg-red-50">
              Supprimer
            </Button>
          )}
        </div>
      </div>

      {showEdit && (
        <div className="space-y-2 p-3 border border-orange-200 dark:border-orange-700 rounded-lg bg-orange-50 dark:bg-orange-900/10">
          <div className="text-xs font-semibold text-orange-700 dark:text-orange-300 mb-1">
            {configured ? 'Nouveau mot de passe' : 'Définir le mot de passe'}
          </div>
          <div>
            <label className="block text-[11px] text-gray-600 dark:text-gray-400 mb-1">Mot de passe (min 4 caractères)</label>
            <Input type="password" value={mdp1} onChange={(e) => setMdp1(e.target.value)}
              placeholder="••••••" autoFocus />
          </div>
          <div>
            <label className="block text-[11px] text-gray-600 dark:text-gray-400 mb-1">Confirmation</label>
            <Input type="password" value={mdp2} onChange={(e) => setMdp2(e.target.value)}
              placeholder="••••••" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="secondary"
              onClick={() => { setShowEdit(false); setMdp1(''); setMdp2(''); }}>
              Annuler
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !mdp1 || !mdp2}
              className="bg-orange-500 hover:bg-orange-600 text-white">
              {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {msg && (
        <div className={`mt-2 p-2 rounded text-xs ${
          msg.type === 'ok'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

export default GeneralTab;
